import classNames from 'classnames'
import IconRender from 'components/common/IconRender'
import Switch from 'components/common/Switch'
import { isMobile } from 'components/utils'
import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomColorsRef, IconSizes } from 'types/general'

import QuantityBubble from './QuantityBubble'

type DropdownProps = {
  icon: string
  label: string
  options: { label: string; value: string }[]
  value: Record<string, boolean>
  onChange: (value: Record<string, boolean>) => void
  onFilter?: () => void
}

const SwitchList = ({
  icon,
  label,
  options,
  value,
  onChange,
  onFilter,
}: DropdownProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const mobile = isMobile()
  const selectedCount = Object.values(value).filter(Boolean).length

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (optionValue: string) => {
    const newValue = {
      ...value,
      [optionValue]: !value[optionValue],
    }
    onChange(newValue)
  }

  const handleClear = () => {
    const newValue = options.reduce(
      (acc, opt) => {
        acc[opt.value] = false
        return acc
      },
      {} as Record<string, boolean>
    )
    onChange(newValue)
  }
  return (
    <div
      className="relative text-sm w-full sm:w-fit"
      ref={dropdownRef}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={classNames('text-left w-full flex items-center relative pl-10 pr-8', {
          'h-10 rounded-lg justify-center': !mobile,
          'h-12 ': mobile,
          'border border-imovPrimary': isOpen && !mobile,
          'border border-gray-300': !isOpen && !mobile,
          'border-b border-gray-300': !isOpen && mobile,
        })}
      >
        <span className="absolute left-3 inset-y-0 flex items-center text-gray-500">
          <IconRender
            icon={icon}
            size={IconSizes.LG}
            color={CustomColorsRef.Primary}
          />
        </span>
        {label && (
          <span className="text-imovPrimary px-2 text-md font-semibold whitespace-nowrap">{t(label)}</span>
        )}
        <span className={classNames("absolute inset-y-0 flex items-center text-gray-500", {
          "right-2": !mobile,
          "right-10": mobile,
        })}>
          <IconRender
            icon={isOpen ? 'regular-chevron-up' : 'regular-chevron-down'}
            size={IconSizes.LG}
            color={CustomColorsRef.Primary}
          />
        </span>
        <QuantityBubble selecteds={selectedCount} />
      </button>

      {(isOpen || mobile) && buttonRef.current && (
        <div
          className={classNames(
            "z-50 bg-white border border-gray-200 rounded-md shadow-lg text-sm transition-all duration-300 ease-in-out",
            {
              "fixed": !mobile,
              "relative": mobile,
              "opacity-0 max-h-0 overflow-hidden": mobile && !isOpen,
              "opacity-100 max-h-96 overflow-visible": mobile && isOpen,
            }
          )}
          style={!mobile ? {
            top: 50,
            width: buttonRef.current.getBoundingClientRect().width,
          } : {}}
        >
          {!mobile && selectedCount > 0 && (
            <div className="flex gap-2 p-2 shadow-sm">
              <button onClick={() => onFilter?.()} className="flex-1 px-3 py-2 bg-imovPrimary text-white rounded cursor-pointer hover:bg-imovPrimaryLight transition-colors">
                {t('common.filter')}
              </button>
              <button onClick={() => handleClear()} className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 rounded cursor-pointer hover:bg-gray-300 transition-colors">
                {t('common.clear')}
              </button>
            </div>
          )}
          {options.map(opt => (
            <div
              key={opt.value}
              className="px-2 py-1 cursor-pointer hover:bg-blue-100"
              onClick={() => handleSelect(opt.value)}
            >
              <Switch
                rightLabel={opt.label}
                name={opt.value}
                value={value[opt.value] || false}
                onChange={() => handleSelect(opt.value)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default SwitchList
